import React, { useEffect, useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { useAuth } from '@/contexts/AuthContext';
import { ROUTES } from '@/lib/constants';
import { AUTH } from '@/constants/testIds';
import { sanitizeInternalNext } from '@/lib/safeNext';

export default function Login() {
  const { login, isAuthenticated, isAdmin, expired, clearExpired } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Fuente ordenada del destino post-login:
  //   1) `?next=` saneado (lo usan EventPaywall / ContentPaywall /
  //      WatchPage / PlansList / DownloadActionButton)
  //   2) `location.state.from` (navegación interna que viene con state)
  //   3) dashboard por defecto
  // `sanitizeInternalNext` descarta cualquier URL absoluta o esquema no
  // interno para prevenir open-redirect.
  const nextQueryRaw = useMemo(
    () => new URLSearchParams(location.search).get('next'),
    [location.search],
  );
  const safeNextFromQuery = sanitizeInternalNext(nextQueryRaw);
  const safeNextFromState = sanitizeInternalNext(location.state?.from);
  const fromPath = safeNextFromQuery || safeNextFromState || ROUTES.dashboard;

  // Propagamos `?next=` al enlace de registro para que, si el usuario
  // decide crear cuenta, el flujo completo vuelva al mismo contenido.
  const registerHref = safeNextFromQuery
    ? `${ROUTES.register}?next=${encodeURIComponent(safeNextFromQuery)}`
    : ROUTES.register;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Único redirect: se dispara cuando isAuthenticated cambia (después del login)
  // o cuando el usuario ya está logueado y vuelve a /acceder.
  useEffect(() => {
    if (!isAuthenticated) return;
    const goesToDefault =
      fromPath === ROUTES.dashboard || fromPath === ROUTES.home;
    const target = isAdmin && goesToDefault ? ROUTES.admin : fromPath;
    navigate(target, { replace: true });
  }, [isAuthenticated, isAdmin, fromPath, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await login({ email: email.trim(), password, rememberMe: remember });
      if (expired) clearExpired();
      // La redirección la maneja el useEffect anterior basándose en isAdmin.
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Bienvenido"
      subtitle="Accede a tu cuenta de NetScouters."
    >
      {expired && (
        <Alert className="mb-4 border-[var(--ns-gold)]/40 bg-[var(--ns-amber-50)]">
          <AlertDescription className="text-[var(--ns-gold-700)]">
            Tu sesión ha expirado. Vuelve a iniciar sesión.
          </AlertDescription>
        </Alert>
      )}
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email" className="text-sm font-medium">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@email.com"
            data-testid={AUTH.loginEmail}
            className="h-11 bg-white"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-sm font-medium">Contraseña</Label>
            <Link
              to={ROUTES.forgot}
              className="text-xs text-[var(--ns-gold-600)] hover:text-[var(--ns-gold-700)] hover:underline"
              data-testid={AUTH.loginForgotLink}
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={showPwd ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid={AUTH.loginPassword}
              className="h-11 bg-white pr-11"
            />
            <button
              type="button"
              onClick={() => setShowPwd((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-[var(--ns-text-muted)] hover:text-[var(--ns-text)]"
              aria-label={showPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              tabIndex={-1}
            >
              {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-[var(--ns-text)] cursor-pointer select-none">
          <Checkbox
            checked={remember}
            onCheckedChange={(v) => setRemember(!!v)}
            data-testid={AUTH.loginRemember}
          />
          Recuérdame
        </label>

        {error && (
          <Alert variant="destructive" data-testid={AUTH.loginError}>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button
          type="submit"
          disabled={submitting}
          data-testid={AUTH.loginSubmit}
          className="h-11 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white hover:shadow-[var(--ns-glow-gold)] border-0 shadow-[var(--ns-shadow-gold)] transition-all"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Acceder'}
        </Button>

        <p className="text-sm text-[var(--ns-text-muted)] text-center">
          ¿Aún no tienes cuenta?{' '}
          <Link
            to={registerHref}
            className="text-[var(--ns-gold-600)] hover:text-[var(--ns-gold-700)] font-semibold hover:underline"
            data-testid={AUTH.loginRegisterLink}
          >
            Regístrate
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
